import { useState } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    InputAdornment,
    Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { FoodsIcon } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import {
    JsonAttribute,
    JsonAttributeType,
    AggregationType,
    useGetAllJsonAttributes,
    useDeleteJsonAttribute,
    useCheckJsonAttributeUsage,
} from "src/api/client-api/jsonAttributes/useJsonAttribute";
import { Loading } from "src/components/utils/Loading";
import { CREATE, JSON_ATTRIBUTE, useFoodpilotContext } from "src/context/FoodpilotContext.tsx";
import { JsonAttributeModal } from "./JsonAttributeModal";
import { useQueryClient } from "react-query";
import { useSnackbar } from "src/utils/useSnackbar";
import { SettingsError } from "./SettingsError";
import { useNavigateSettingsParent } from "./useNavigateSettingsParent";
import { JSON_ATTRIBUTE_ENTITY_CLASS } from "src/constants";

export const JsonAttributes = () => {
    const { canUser } = useFoodpilotContext();
    const [searchTerm, setSearchTerm] = useState("");

    const { jsonAttributes, isLoading, isError } = useGetAllJsonAttributes();
    const deleteJsonAttribute = useDeleteJsonAttribute();
    const checkJsonAttributeUsage = useCheckJsonAttributeUsage();
    const navigateSettingsParent = useNavigateSettingsParent();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const snackbar = useSnackbar();

    const [displayDialog, setDisplayDialog] = useState(false);
    const [idToDelete, setIdToDelete] = useState<number | undefined>(undefined);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingJsonAttribute, setEditingJsonAttribute] = useState<JsonAttribute | undefined>(undefined);

    if (isLoading) return <Loading />;

    if (isError) {
        return (
            <SettingsError
                heading={t("jsonAttributes.title", "Additional Object Parameters")}
                message={t(
                    "jsonAttributes.error.loadFailed",
                    "Failed to load custom attributes. Please try refreshing the page or contact support if the problem persists.",
                )}
            />
        );
    }

    const handleDeleteClick = (id: number) => {
        setIdToDelete(id);
        setDisplayDialog(true);
    };

    const handleOpenCreateModal = () => {
        setEditingJsonAttribute(undefined);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (jsonAttribute: JsonAttribute) => {
        setEditingJsonAttribute(jsonAttribute);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingJsonAttribute(undefined);
    };

    const handleModalSuccess = () => {
        queryClient.invalidateQueries({ queryKey: ["getAllJsonAttributes"] });
        snackbar.setSnackbarProps({
            message: editingJsonAttribute ? "Attribute updated successfully" : "Attribute created successfully",
            type: "success",
        });
    };

    const filteredJsonAttributes = (jsonAttributes || []).filter((attr) =>
        attr.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const renderConfirmDialog = () => (
        <Dialog
            open={displayDialog}
            onClose={() => setDisplayDialog(false)}
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    padding: "32px",
                    width: "400px",
                },
            }}
        >
            <Box sx={{ textAlign: "center" }}>
                <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E", marginBottom: "16px" }}>
                    {t("jsonAttribute.delete.confirm.title", "Confirm Deletion")}
                </Typography>
                <Typography variant="small" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "24px" }}>
                    {t("jsonAttribute.delete.confirm.message", "Are you sure you want to delete this attribute?")}
                </Typography>
                <Box sx={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                    <Button
                        variant="outlined"
                        onClick={() => setDisplayDialog(false)}
                        sx={{
                            borderRadius: "44px",
                            padding: "0px 20px",
                            height: "40px",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            borderColor: "#E7E6E4",
                            color: "#393A36",
                        }}
                    >
                        {t("cancel", "Cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (idToDelete) {
                                deleteJsonAttribute.mutate(idToDelete, {
                                    onSuccess: () => {
                                        queryClient.invalidateQueries({ queryKey: ["getAllJsonAttributes"] });
                                        snackbar.setSnackbarProps({
                                            message: "Attribute deleted successfully",
                                            type: "success",
                                        });
                                        setDisplayDialog(false);
                                        setIdToDelete(undefined);
                                    },
                                    onError: () => {
                                        snackbar.setSnackbarProps({
                                            message: "Failed to delete attribute. Please try again.",
                                            type: "error",
                                        });
                                    },
                                });
                            }
                        }}
                        sx={{
                            borderRadius: "44px",
                            padding: "0px 20px",
                            height: "40px",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            background: "linear-gradient(180deg, #FF6B6B 0%, #FF5252 100%)",
                            color: "white",
                        }}
                    >
                        {t("delete", "Delete")}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );

    const getEntityDisplayName = (entityClass: string) => {
        switch (entityClass) {
            case JSON_ATTRIBUTE_ENTITY_CLASS.product:
                return t("entity.product", "Product");
            case JSON_ATTRIBUTE_ENTITY_CLASS.ingredient:
                return t("entity.ingredient", "Ingredient");
            case JSON_ATTRIBUTE_ENTITY_CLASS.organization:
                return t("entity.company", "Company");
            case JSON_ATTRIBUTE_ENTITY_CLASS.user:
                return t("entity.user", "User");
            default:
                return entityClass;
        }
    };

    const getTypeDisplayName = (type: JsonAttributeType) => {
        switch (type) {
            case JsonAttributeType.STRING:
                return t("jsonAttribute.types.string", "String");
            case JsonAttributeType.INTEGER:
                return t("jsonAttribute.types.int", "Integer");
            case JsonAttributeType.FLOAT:
                return t("jsonAttribute.types.float", "Float");
            case JsonAttributeType.BOOLEAN:
                return t("jsonAttribute.types.bool", "Boolean");
            default:
                return type;
        }
    };

    const getAggregationDisplayName = (aggregationType: AggregationType | null | undefined) => {
        if (!aggregationType) {
            return t("jsonAttribute.aggregationType.none", "None");
        }
        switch (aggregationType) {
            case AggregationType.SUM:
                return t("jsonAttribute.aggregationType.sum", "Sum");
            case AggregationType.AVERAGE:
                return t("jsonAttribute.aggregationType.average", "Average");
            case AggregationType.NONE:
                return t("jsonAttribute.aggregationType.none", "None");
            default:
                return aggregationType;
        }
    };

    return (
        <Box sx={{ padding: "32px" }}>
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid #E7E6E4",
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "32px",
                        borderBottom: "1px solid #E7E6E4",
                        backgroundColor: "white",
                    }}
                >
                    <Box
                        sx={{
                            width: "28px",
                            height: "28px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                        }}
                        onClick={navigateSettingsParent}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                "& svg": {
                                    strokeWidth: "2px",
                                    stroke: "currentColor",
                                },
                            }}
                        >
                            <FoodsIcon icon="arrowLeftShort" size={6} />
                        </Box>
                    </Box>
                    <Box sx={{ marginLeft: "16px" }}>
                        <Typography variant="number-inter" sx={{ lineHeight: "1.6em", color: "#1E1E1E" }}>
                            {t("jsonAttributes.title", "Additional Object Parameters")}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ padding: "32px", backgroundColor: "#FEFCFB" }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "24px",
                        }}
                    >
                        <TextField
                            placeholder={t("jsonAttribute.search.placeholder", "Search attributes...")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{
                                width: "300px",
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "44px",
                                    backgroundColor: "white",
                                    height: "40px",
                                    border: "1px solid #E7E6E4",
                                    "& fieldset": {
                                        border: "none",
                                    },
                                    "&:hover fieldset": {
                                        border: "none",
                                    },
                                    "&.Mui-focused fieldset": {
                                        border: "none",
                                    },
                                },
                                "& .MuiInputBase-input": {
                                    fontSize: "0.875rem",
                                    fontWeight: 400,
                                    lineHeight: "1.6em",
                                    color: "#393A36",
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "#A3A2A0", fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {canUser(CREATE, JSON_ATTRIBUTE) && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon sx={{ fontSize: 20 }} />}
                                onClick={handleOpenCreateModal}
                                sx={(theme) => ({
                                    background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                    borderRadius: "44px",
                                    px: 2.5,
                                    py: 1.25,
                                    height: "40px",
                                    textTransform: "none",
                                    fontSize: "0.875rem",
                                    fontWeight: 700,
                                    color: "white",
                                    boxShadow: "0px 4px 8px 0px rgba(42, 43, 39, 0.08)",
                                    "&:hover": {
                                        background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                        boxShadow: "0px 6px 12px 0px rgba(42, 43, 39, 0.12)",
                                    },
                                })}
                            >
                                {t("jsonAttribute.add", "Add a new additional object parameter")}
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Content area */}
                <Box sx={{ padding: "0px 32px 24px" }}>
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                            border: "1px solid #E7E6E4",
                            borderRadius: "8px",
                            overflow: "hidden",
                        }}
                    >
                        <Table sx={{ "& .MuiTableCell-root": { border: "none" } }}>
                            <TableHead>
                                <TableRow
                                    sx={{
                                        backgroundColor: "#FEFCFB",
                                        "& .MuiTableCell-root": {
                                            borderBottom: "1px solid #E7E6E4",
                                            borderRight: "1px solid #E7E6E4",
                                            padding: "0px 14px 0px 20px",
                                            height: "48px",
                                            "&:last-child": {
                                                borderRight: "none",
                                            },
                                        },
                                    }}
                                >
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t("jsonAttribute.table.label", "Label")}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t("jsonAttribute.table.identifier", "Identifier")}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t("jsonAttribute.table.appliesTo", "Applies To")}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t("jsonAttribute.table.type", "Type")}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t("jsonAttribute.table.aggregationType", "Aggregation Type")}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t("actions", "Actions")}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredJsonAttributes.length === 0 ?
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                                            <Typography variant="small" sx={{ color: "#A3A2A0" }}>
                                                {searchTerm ?
                                                    t(
                                                        "jsonAttribute.table.noResults",
                                                        "No attributes match your search.",
                                                    )
                                                :   t("jsonAttribute.table.empty", "No attributes defined yet.")}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                :   filteredJsonAttributes.map((attr, index) => (
                                        <TableRow
                                            key={attr.id}
                                            sx={{
                                                "& .MuiTableCell-root": {
                                                    borderRight: "1px solid #E7E6E4",
                                                    padding: "0px 14px 0px 20px",
                                                    height: "58px",
                                                    "&:last-child": {
                                                        borderRight: "none",
                                                        padding: "0px 8px",
                                                    },
                                                },
                                                "&:hover": {
                                                    backgroundColor: "#F9F9F8",
                                                },
                                            }}
                                        >
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Typography
                                                    variant="small-medium"
                                                    sx={{ lineHeight: "22px", color: "#1E1E1E", fontWeight: 600 }}
                                                >
                                                    {attr.label}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Typography
                                                    variant="small-medium"
                                                    sx={{ lineHeight: "1.6em", color: "#393A36" }}
                                                >
                                                    {attr.identifier}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Typography
                                                    variant="small-medium"
                                                    sx={{ lineHeight: "1.6em", color: "#393A36" }}
                                                >
                                                    {getEntityDisplayName(attr.entityClass)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Typography
                                                    variant="small-medium"
                                                    sx={{ lineHeight: "1.6em", color: "#393A36" }}
                                                >
                                                    {getTypeDisplayName(attr.type)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Typography
                                                    variant="small-medium"
                                                    sx={{ lineHeight: "1.6em", color: "#393A36" }}
                                                >
                                                    {getAggregationDisplayName(attr.aggregationType)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    {attr.canModify && (
                                                        <Box
                                                            component="button"
                                                            onClick={() => handleOpenEditModal(attr)}
                                                            sx={{
                                                                width: "32px",
                                                                height: "32px",
                                                                borderRadius: "50%",
                                                                border: "none",
                                                                backgroundColor: "transparent",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                cursor: "pointer",
                                                                transition: "background-color 0.2s",
                                                                "&:hover": {
                                                                    backgroundColor: "#F5F5F5",
                                                                },
                                                            }}
                                                        >
                                                            <FoodsIcon icon="edit" size={1} />
                                                        </Box>
                                                    )}
                                                    {attr.canDelete && (
                                                        <Box
                                                            component="button"
                                                            onClick={() => handleDeleteClick(attr.id)}
                                                            sx={{
                                                                width: "32px",
                                                                height: "32px",
                                                                borderRadius: "50%",
                                                                border: "none",
                                                                backgroundColor: "transparent",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                cursor: "pointer",
                                                                transition: "background-color 0.2s",
                                                                "&:hover": {
                                                                    backgroundColor: "#F5F5F5",
                                                                },
                                                            }}
                                                        >
                                                            <FoodsIcon icon="delete" size={1} />
                                                        </Box>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Paper>

            {renderConfirmDialog()}

            <JsonAttributeModal
                open={isModalOpen}
                onClose={handleCloseModal}
                jsonAttribute={editingJsonAttribute}
                onSuccess={handleModalSuccess}
            />
        </Box>
    );
};
